// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R
/* eslint-disable @typescript-eslint/no-explicit-any */

import { createAxios } from 'ts/createAxios';
import { getApiPath } from 'util/commons';
import { API_ENDPOINTS } from 'util/endPoints';
import { handleError } from 'util/errorHandler';
import {
  CollectRoute,
  CollectRouteDetail,
  CollectRouteSiteItem,
  CollectRouteSiteList,
  DriverItemType,
  EmissionSite,
  MaterialNameItemType,
  QRCodeItemType,
  RouteCategoryItemType,
  RouteForm,
  RouteFormUpdate,
  SiteItemType,
  EmissionItem,
  Memo,
  CollectRouteSiteInfo,
} from 'types/routeManagementTypes';
import { store } from 'redux/store';
import { setCollectedTotalWeight } from 'redux/slices/collectingRoute';

export const getCollectRouteEmissionSites = async (params?: {
  successCallback?: (data: { sites: SiteItemType[] }) => void;
  failedCallback?: (error: any) => void;
}) => {
  const { successCallback, failedCallback } = params || {};

  try {
    const { data } = await createAxios().get<{ sites: SiteItemType[] }>(
      API_ENDPOINTS.SITE
    );

    successCallback?.(data);
  } catch (e) {
    handleError(e);
    failedCallback?.(e);
  }
};

export const getCollectRouteDriver = async (params: {
  id: string;
  successCallback?: (data: { drivers: DriverItemType[] }) => void;
  failedCallback?: (error: any) => void;
}) => {
  const { successCallback, failedCallback } = params || {};

  try {
    const { data } = await createAxios().get<{ drivers: DriverItemType[] }>(
      getApiPath(API_ENDPOINTS.COLLECT_DRIVERS, { id: params.id })
    );

    successCallback?.(data);
  } catch (e) {
    handleError(e);
    failedCallback?.(e);
  }
};

export const getCollectRouteMaterialNames = async (params: {
  id: string;
  successCallback?: (data: MaterialNameItemType[]) => void;
  failedCallback?: (error: any) => void;
}) => {
  const { successCallback, failedCallback } = params || {};

  try {
    const { data } = await createAxios().get<MaterialNameItemType[]>(
      getApiPath(API_ENDPOINTS.COLLECT_MATERIAL_NAMES, { id: params.id })
    );

    successCallback?.(data);
  } catch (e) {
    handleError(e);
    failedCallback?.(e);
  }
};

export const getRouteCategories = async (params?: {
  driverId?: string;
  hasRoutes?: boolean;
  successCallback?: (data: RouteCategoryItemType[]) => void;
  failedCallback?: (error: any) => void;
  finallyCallback?: () => void;
}) => {
  const {
    driverId,
    hasRoutes,
    successCallback,
    failedCallback,
    finallyCallback,
  } = params || {};

  try {
    const { data } = await createAxios().get<RouteCategoryItemType[]>(
      API_ENDPOINTS.ROUTE_CATEGORIES,
      {
        params: { driverId, hasRoutes },
      }
    );

    successCallback?.(data);
  } catch (e) {
    handleError(e);
    failedCallback?.(e);
  } finally {
    finallyCallback?.();
  }
};

export const createCollectRoute = async (p: {
  params: RouteForm;
  successCallback: () => void;
}) => {
  const { params, successCallback } = p;

  try {
    await createAxios().post(API_ENDPOINTS.COLLECT_ROUTES, params);

    successCallback();
  } catch (error) {
    handleError(error);
  }
};

export const updateCollectRoute = async (p: {
  id: string;
  params: RouteFormUpdate;
  successCallback: () => void;
}) => {
  const { id, params, successCallback } = p;

  try {
    await createAxios().patch(
      getApiPath(API_ENDPOINTS.COLLECT_ROUTE_DETAIL, { id }),
      params
    );

    successCallback();
  } catch (error) {
    handleError(error);
  }
};

export const getCollectRouteDetail = async (params: {
  id: number;
  successCallback?: (data: CollectRouteDetail) => void;
  failedCallback?: (error: any) => void;
}) => {
  const { successCallback, failedCallback } = params || {};

  try {
    const { data } = await createAxios().get<CollectRouteDetail>(
      getApiPath(API_ENDPOINTS.COLLECT_ROUTE_DETAIL, { id: params.id })
    );

    successCallback?.(data);
  } catch (e) {
    handleError(e);
    failedCallback?.(e);
  }
};

export const getCollectRoutes = async (p?: {
  params: {
    screen: 'list' | 'setting';
    keyword?: string;
    category?: string;
    driverId?: string;
    includeDeletedSite?: boolean;
  };
  successCallback?: (data: CollectRoute[]) => void;
  failedCallback?: (error: any) => void;
  finallyCallback?: () => void;
}) => {
  const { successCallback, failedCallback, finallyCallback, params } = p || {};

  try {
    const { data } = await createAxios().get<CollectRoute[]>(
      API_ENDPOINTS.COLLECT_ROUTES,
      {
        params,
      }
    );

    successCallback?.(data);
  } catch (e) {
    handleError(e);
    failedCallback?.(e);
  } finally {
    finallyCallback?.();
  }
};

export const getEmissionSites = async (params: {
  query?: {
    siteId?: string;
    keyword?: string;
    materialNames?: string;
  };
  successCallback?: (data: EmissionSite[]) => void;
  failedCallback?: (error: any) => void;
}) => {
  const { successCallback, failedCallback } = params || {};

  try {
    const { data } = await createAxios().get<EmissionSite[]>(
      API_ENDPOINTS.COLLECT_EMISSION_SITES.replace(
        ':id',
        String(params.query?.siteId || '')
      ),
      {
        params: {
          keyword: params.query?.keyword,
          materialNames: params.query?.materialNames,
        },
      }
    );

    successCallback?.(data);
  } catch (e) {
    handleError(e);
    failedCallback?.(e);
  }
};

export const addCollectRouteSites = async (params: {
  routeId: number;
  siteIds: string[];
  successCallback: () => void;
}) => {
  const { routeId, siteIds, successCallback } = params;
  try {
    await createAxios().post(
      getApiPath(API_ENDPOINTS.COLLECT_ROUTE_DETAIL_SITES, { id: routeId }),
      {
        siteIds,
      }
    );

    successCallback();
  } catch (error) {
    handleError(error);
  }
};

export const deleteRoute = async (params: {
  routeId: number;
  successCallback: () => void;
}) => {
  const { routeId, successCallback } = params;
  try {
    await createAxios().delete(
      getApiPath(API_ENDPOINTS.COLLECT_ROUTE_DETAIL, { id: routeId })
    );

    successCallback();
  } catch (error) {
    handleError(error);
  }
};

export const getCollectRouteSites = async (params: {
  routeId: number | string;
  keyword?: string;
  successCallback?: (data: CollectRouteSiteList) => void;
  failedCallback?: (error: any) => void;
  finallyCallback?: () => void;
}) => {
  const { routeId, successCallback, failedCallback, finallyCallback, keyword } =
    params;

  try {
    if (routeId === 'all' || routeId === 'none') {
      const { data } = await createAxios().get<CollectRouteSiteInfo[]>(
        API_ENDPOINTS.COLLECT_ROUTE_ALL_SITES,
        {
          params: {
            keyword,
          },
        }
      );

      successCallback?.({
        sites: data,
      });
    } else {
      const { data } = await createAxios().get<CollectRouteSiteList>(
        getApiPath(API_ENDPOINTS.COLLECT_ROUTE_DETAIL_SITES, { id: routeId }),
        {
          params: {
            keyword,
          },
        }
      );

      successCallback?.(data);
    }
  } catch (error) {
    handleError(error);
    failedCallback?.(error);
  } finally {
    finallyCallback?.();
  }
};

export const getCollectRouteQRInfo = async (params: {
  siteId: string;
  qrCode: string;
  successCallback?: (data: QRCodeItemType) => void;
  failedCallback?: (error: any) => void;
}) => {
  const { siteId, qrCode, successCallback, failedCallback } = params;

  try {
    const { data } = await createAxios().get<QRCodeItemType>(
      getApiPath(API_ENDPOINTS.COLLECT_QR_SCAN, { siteId, qrCode })
    );

    successCallback?.(data);
  } catch (error) {
    handleError(error);
    failedCallback?.(error);
  }
};

export const getEmissionsBySiteAndRoute = async (params: {
  query: {
    routeId: number | string;
    siteId: string;
    dateFrom: string;
    dateTo: string;
  };
  successCallback?: (data: any) => void;
  failedCallback?: (error: any) => void;
}) => {
  const { query, successCallback, failedCallback } = params;
  const { routeId, siteId } = query;

  try {
    const { data } = await createAxios().get<EmissionItem[]>(
      getApiPath(
        routeId === 'none' || routeId === 'all'
          ? API_ENDPOINTS.COLLECT_EMISSIONS_BY_SITE_IN_ROUTES
          : API_ENDPOINTS.COLLECT_EMISSIONS_BY_SITE_AND_ROUTE,
        {
          routeId,
          siteId,
        }
      ),
      {
        params: {
          dateFrom: query.dateFrom,
          dateTo: query.dateTo,
        },
      }
    );

    successCallback?.(data);
  } catch (error) {
    handleError(error);
    failedCallback?.(error);
  }
};

export const submitCollectRouteQR = async (params: {
  routeId?: number;
  areaId: string;
  emissionIds: string[];
  driverId?: string;
  successCallback?: () => void;
  failedCallback?: (error: any) => void;
}) => {
  const {
    routeId,
    areaId,
    emissionIds,
    driverId,
    successCallback,
    failedCallback,
  } = params;

  try {
    await createAxios({ prefix: 'v2' }).post(API_ENDPOINTS.COLLECTS, {
      routeId,
      areaId,
      emissionIds,
      driverId,
    });

    successCallback?.();
  } catch (error) {
    handleError(error);
    failedCallback?.(error);
  }
};

export const collectEmissionItems = async (params: {
  formData: {
    areaId: string;
    emissionIds: string[];
    routeId?: number;
    driverId?: string;
  };
  successCallback?: (data: any) => void;
  failedCallback?: (error: any) => void;
}) => {
  const { formData, successCallback, failedCallback } = params;

  try {
    const { data } = await createAxios({ prefix: 'v2' }).post<EmissionItem[]>(
      API_ENDPOINTS.COLLECTS,
      formData
    );

    successCallback?.(data);
  } catch (error) {
    handleError(error);
    failedCallback?.(error);
  }
};

export const getEmissionSiteMemo = async (params: {
  siteId: string;
  successCallback?: (data: Memo) => void;
  failedCallback?: (error: any) => void;
}) => {
  const { siteId, successCallback, failedCallback } = params;

  try {
    const { data } = await createAxios().get<Memo>(
      getApiPath(API_ENDPOINTS.COLLECT_ROUTES_SITE_MEMO, {
        siteId,
      })
    );

    successCallback?.(data);
  } catch (error) {
    handleError(error);
    failedCallback?.(error);
  }
};

export const updateEmissionSiteMemo = async (params: {
  siteId: string;
  memo: string;
  successCallback?: (data: any) => void;
  failedCallback?: (error: any) => void;
}) => {
  const { siteId, memo, successCallback, failedCallback } = params;

  try {
    const { data } = await createAxios().put<Memo>(
      getApiPath(API_ENDPOINTS.COLLECT_ROUTES_SITE_MEMO, {
        siteId,
      }),
      { memo }
    );

    successCallback?.(data);
  } catch (error) {
    handleError(error);
    failedCallback?.(error);
  }
};

export const getCollectRouteItems = async (params: {
  routeIds?: number[];
  successCallback?: (data: CollectRouteSiteItem[]) => void;
  failedCallback?: (error: any) => void;
}) => {
  const { routeIds, successCallback, failedCallback } = params;

  try {
    const { data } = await createAxios().post(
      API_ENDPOINTS.COLLECT_ROUTE_ITEMS,
      {
        routeIds,
      }
    );

    successCallback?.(data);
  } catch (error) {
    handleError(error);
    failedCallback?.(error);
  }
};

export const getRoutesCollectedTotalWeight = async () => {
  try {
    const { data } = await createAxios().get<{
      totalWeight: string;
    }>(API_ENDPOINTS.COLLECT_ROUTES_TOTAL_WEIGHT);

    store.dispatch(setCollectedTotalWeight(data.totalWeight));
  } catch (error) {
    store.dispatch(setCollectedTotalWeight(undefined));

    handleError(error);
  }
};
